import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../hooks/useNotifications';
import { playNotificationChime } from '../utils/notificationSound';

const AUTO_DISMISS_MS = 12000;

// Toast en vivo + sonido para lo que llega mientras el admin tiene el panel
// abierto — complementa la campana (que solo actualiza el contador). Monta
// UNA sola vez en AdminLayout para no duplicar sonido si se abriera en más
// de un lugar a la vez.
export default function WorkflowAlertToast() {
  const { notifications, markAttended, markRead } = useNotifications();
  const navigate = useNavigate();
  const [toasts, setToasts] = useState([]);
  const initializedRef = useRef(false);
  const seenIdsRef = useRef(new Set());

  useEffect(() => {
    // Primera carga: no avisar de notificaciones viejas que ya estaban ahí
    // antes de abrir la pestaña — solo lo que llega después cuenta como "en vivo".
    if (!initializedRef.current) {
      notifications.forEach((n) => seenIdsRef.current.add(n.id));
      initializedRef.current = true;
      return;
    }

    const fresh = notifications.filter((n) => !seenIdsRef.current.has(n.id));
    if (!fresh.length) return;
    fresh.forEach((n) => seenIdsRef.current.add(n.id));

    playNotificationChime(fresh.some((n) => n.requiresAction));
    setToasts((prev) => [...fresh, ...prev].slice(0, 5));
  }, [notifications]);

  useEffect(() => {
    if (!toasts.length) return undefined;
    const timers = toasts.map((t) =>
      setTimeout(() => dismiss(t.id), AUTO_DISMISS_MS)
    );
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toasts.map((t) => t.id).join(',')]);

  const dismiss = (id) => {
    markRead(id);
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  const handleOpen = (t) => {
    markAttended(t.id);
    dismiss(t.id);
    if (t.route) navigate(t.route);
  };

  if (!toasts.length) return null;

  return (
    <div className="fixed bottom-5 right-5 z-[9999] flex w-80 max-w-[90vw] flex-col gap-2">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`rounded-xl border bg-white p-3 shadow-lg transition-opacity ${
            t.requiresAction ? 'border-red-200' : 'border-gray-200'
          }`}
        >
          <div className="flex items-start justify-between gap-2">
            <p className="text-sm font-medium leading-snug text-gray-800">{t.message}</p>
            <button
              type="button"
              onClick={() => dismiss(t.id)}
              className="flex-shrink-0 text-gray-300 hover:text-gray-500"
              aria-label="Cerrar"
            >
              ×
            </button>
          </div>
          {t.route && (
            <button
              type="button"
              onClick={() => handleOpen(t)}
              className="mt-2 rounded-lg bg-orange-500 px-3 py-1 text-xs font-semibold text-white hover:bg-orange-600"
            >
              Ver ahora →
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
